import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import {
  changeMyPassword,
  totpDisable,
  totpEnable,
  totpSetup,
  type TOTPSetupResponse,
} from '../../lib/panelApi';

type Props = {
  accessToken: string;
  adminEmail: string;
  adminRole: string;
  totpEnabled: boolean;
  onTotpChanged: () => void;
};

export function AccountPage({ accessToken, adminEmail, adminRole, totpEnabled, onTotpChanged }: Props) {
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
      <ProfileCard email={adminEmail} role={adminRole} />
      <ChangePasswordCard accessToken={accessToken} />
      <TwoFactorCard
        accessToken={accessToken}
        enabled={totpEnabled}
        accountLabel={adminEmail}
        onChanged={onTotpChanged}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Profile (read-only — email is intentionally NOT editable for any
// role per the product rule).
// ────────────────────────────────────────────────────────────────

function ProfileCard({ email, role }: { email: string; role: string }) {
  return (
    <div className="card">
      <div className="sectionTitle">Profile</div>
      <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Your account email is fixed and cannot be changed by yourself or another admin.
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        <Row label="Email" value={email} />
        <Row label="Role" value={role} />
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
      <div style={{ minWidth: 80 }} className="muted">
        {label}
      </div>
      <div style={{ fontWeight: 600 }}>{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Change password
// ────────────────────────────────────────────────────────────────

function ChangePasswordCard({ accessToken }: { accessToken: string }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setOk(false);
    setError(null);

    if (!oldPassword || !newPassword) {
      setError('Old password and new password are required.');
      return;
    }
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setError('Password confirmation does not match.');
      return;
    }

    setLoading(true);
    try {
      await changeMyPassword(accessToken, { old_password: oldPassword, new_password: newPassword });
      setOldPassword('');
      setNewPassword('');
      setConfirm('');
      setOk(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Change password failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="card">
      <div className="sectionTitle">Change password</div>
      <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
        Password must be at least 6 characters.
      </div>

      <form onSubmit={onSubmit}>
        <div className="field" style={{ marginTop: 0 }}>
          <div className="label">Old password</div>
          <input value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} type="password" autoComplete="current-password" />
        </div>
        <div className="field">
          <div className="label">New password</div>
          <input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} type="password" autoComplete="new-password" />
        </div>
        <div className="field">
          <div className="label">Confirm new password</div>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} type="password" autoComplete="new-password" />
        </div>

        {error ? (
          <div style={{ marginTop: 12 }}>
            <div className="errorBox">{error}</div>
          </div>
        ) : null}

        {ok ? (
          <div style={{ marginTop: 12 }}>
            <div className="pill pill-ok">Password updated</div>
          </div>
        ) : null}

        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? 'Saving…' : 'Update password'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Two-factor authentication (Google Authenticator)
// ────────────────────────────────────────────────────────────────

function TwoFactorCard({
  accessToken,
  enabled,
  accountLabel,
  onChanged,
}: {
  accessToken: string;
  enabled: boolean;
  accountLabel: string;
  onChanged: () => void;
}) {
  return (
    <div className="card">
      <div className="sectionTitle">Two-factor authentication</div>
      <div className="muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Add a one-time code from Google Authenticator (or any TOTP-compatible app like Authy or 1Password) to your sign-in.
      </div>

      {enabled ? (
        <DisableTOTP accessToken={accessToken} onDone={onChanged} />
      ) : (
        <EnrollTOTP accessToken={accessToken} accountLabel={accountLabel} onDone={onChanged} />
      )}
    </div>
  );
}

function EnrollTOTP({
  accessToken,
  accountLabel,
  onDone,
}: {
  accessToken: string;
  accountLabel: string;
  onDone: () => void;
}) {
  const [setup, setSetup] = useState<TOTPSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');

  // Render the TOTP QR locally. Sending the otpauth URL (which embeds
  // the raw shared secret) to a third-party QR API would hand them the
  // second factor — the QR must never leave this browser.
  useEffect(() => {
    let alive = true;
    if (!setup?.otpauth_url) {
      setQrDataUrl('');
      return;
    }
    QRCode.toDataURL(setup.otpauth_url, { width: 200, margin: 1 })
      .then((url) => {
        if (alive) setQrDataUrl(url);
      })
      .catch(() => {
        if (alive) setQrDataUrl('');
      });
    return () => {
      alive = false;
    };
  }, [setup?.otpauth_url]);

  async function startSetup() {
    setError(null);
    setLoading(true);
    try {
      const res = await totpSetup(accessToken);
      setSetup(res);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start 2FA setup';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function confirmEnable(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Authenticator code must be 6 digits.');
      return;
    }
    setLoading(true);
    try {
      await totpEnable(accessToken, code);
      setSetup(null);
      setCode('');
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to enable 2FA';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  if (!setup) {
    return (
      <div>
        <div style={{ marginBottom: 12 }}>
          <span className="pill">Status: <b>disabled</b></span>
        </div>
        <button className="btn btn-primary" onClick={startSetup} disabled={loading}>
          {loading ? 'Generating…' : 'Enable two-factor authentication'}
        </button>
        {error ? (
          <div style={{ marginTop: 12 }}>
            <div className="errorBox">{error}</div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <ol style={{ paddingLeft: 18, lineHeight: 1.7, marginTop: 0 }}>
        <li>
          Open Google Authenticator and tap <b>Add</b> → <b>Scan a QR code</b>, or pick <b>Enter a setup key</b>.
        </li>
        <li>
          Scan this QR code, or paste the secret manually:
        </li>
      </ol>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
        {qrDataUrl ? (
          <img
            alt="QR code for two-factor authentication"
            width={180}
            height={180}
            style={{ borderRadius: 8, background: '#fff', padding: 8 }}
            src={qrDataUrl}
          />
        ) : (
          <div
            style={{ width: 180, height: 180, borderRadius: 8, background: 'rgba(10,25,47,0.06)' }}
            aria-label="Generating QR code"
          />
        )}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="muted" style={{ fontSize: 12 }}>Account</div>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{accountLabel}</div>
          <div className="muted" style={{ fontSize: 12 }}>Secret (base32)</div>
          <code
            style={{
              display: 'block',
              wordBreak: 'break-all',
              padding: 8,
              background: 'rgba(10,25,47,0.06)',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {setup.secret}
          </code>
        </div>
      </div>

      <form onSubmit={confirmEnable} style={{ marginTop: 16 }}>
        <div className="field" style={{ marginTop: 0 }}>
          <div className="label">Enter the 6-digit code from the app to finish</div>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
            inputMode="numeric"
            maxLength={6}
            placeholder="123456"
            style={{ letterSpacing: '0.3em', fontVariantNumeric: 'tabular-nums', fontSize: 18 }}
          />
        </div>

        {error ? (
          <div style={{ marginTop: 12 }}>
            <div className="errorBox">{error}</div>
          </div>
        ) : null}

        <div className="row" style={{ marginTop: 12, gap: 8 }}>
          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? 'Verifying…' : 'Verify and enable'}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setSetup(null);
              setCode('');
              setError(null);
            }}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function DisableTOTP({ accessToken, onDone }: { accessToken: string; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!password || !/^\d{6}$/.test(code)) {
      setError('Password and 6-digit code are required.');
      return;
    }
    setLoading(true);
    try {
      await totpDisable(accessToken, { password, code });
      setPassword('');
      setCode('');
      setConfirming(false);
      onDone();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to disable 2FA';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  if (!confirming) {
    return (
      <div>
        <div style={{ marginBottom: 12 }}>
          <span className="pill pill-ok">Status: <b>enabled</b></span>
        </div>
        <button className="btn" onClick={() => setConfirming(true)}>
          Disable two-factor authentication
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
        Confirm your password and a current 6-digit code to disable 2FA.
      </div>
      <div className="field" style={{ marginTop: 0 }}>
        <div className="label">Password</div>
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="current-password" />
      </div>
      <div className="field">
        <div className="label">6-digit code</div>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
          inputMode="numeric"
          maxLength={6}
          placeholder="123456"
          style={{ letterSpacing: '0.3em', fontVariantNumeric: 'tabular-nums', fontSize: 18 }}
        />
      </div>

      {error ? (
        <div style={{ marginTop: 12 }}>
          <div className="errorBox">{error}</div>
        </div>
      ) : null}

      <div className="row" style={{ marginTop: 12, gap: 8 }}>
        <button className="btn btn-primary" type="submit" disabled={loading}>
          {loading ? 'Disabling…' : 'Disable 2FA'}
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setConfirming(false);
            setPassword('');
            setCode('');
            setError(null);
          }}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
